/**
 * Stale Build Reload
 * Reloads the page once per build when a lazily loaded file fails to load, so a tab opened before
 * a deploy picks up the new build instead of showing "Failed to fetch dynamically imported module".
 * @packageDocumentation
 */

/** sessionStorage key holding the entry script of the build the page was last reloaded for. */
const RELOADED_FOR_BUILD_KEY = 'nexus:reloaded-for-build';

/** Set once a reload has started, so several failures in the same moment start only one reload. */
let reloadStarted = false;

/**
 * Starts a page reload unless this build was already reloaded once in this tab.
 *
 * The build is identified by the entry script's `src` (`/assets/index-<hash>.js`), which changes
 * on every deploy that renames a chunk. When the stored value equals the current entry script,
 * this build already failed after a reload, so the error is left to the error screen. When
 * sessionStorage is unavailable, no reload is started, because it could not be limited to one.
 *
 * @returns { boolean } True if a reload is under way (started by this call or an earlier one).
 */
export const reloadOnceForNewBuild = (): boolean => {
  if (reloadStarted) {
    return true;
  }

  const currentBuild = document.querySelector('script[type="module"][src]')?.getAttribute('src');

  if (!currentBuild) {
    return false;
  }

  try {
    if (sessionStorage.getItem(RELOADED_FOR_BUILD_KEY) === currentBuild) {
      return false;
    }

    sessionStorage.setItem(RELOADED_FOR_BUILD_KEY, currentBuild);
  } catch {
    return false;
  }

  reloadStarted = true;
  globalThis.location.reload();

  return true;
};
